import React, { useState } from "react";
import "react-phone-number-input/style.css";
import PhoneInput, { isPossiblePhoneNumber } from "react-phone-number-input";
import axios from "axios";
import { parsePhoneNumberWithError } from "libphonenumber-js";

interface CallbackFormProps {
  onClose: () => void;
  onSuccessClose: () => void;
}

const CallbackForm = ({
  onClose,
  onSuccessClose,
}: CallbackFormProps) => {
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [dateAndTime, setDateAndTime] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [reasonForCallback, setReasonForCallback] = useState("");

  type ReasonToSkillIdMapping = {
    [key: string]: string;
  };

  const reasonToSkillId: ReasonToSkillIdMapping = {
    Claim: "8a7ca3d4-67ce-4910-b862-3cda4d22357c",
    Renewal: "d8303643-90b5-4d14-ac7c-fdb23a515c84",
    Quote: "abdfa6d4-e46f-437e-8eb8-b8918c48c4bb",
    "General Query": "bec2ac29-df70-4f7c-a1b9-ec427134f1b2",
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const selectedDate = new Date(dateAndTime);
    const currentDate = new Date();

    if (selectedDate < currentDate) {
      setPopupMessage(
        "Callback requests cannot be scheduled in the past. Please select a future date and time."
      );
      setShowPopup(true);
      return;
    }

    if (selectedDate.getDay() === 0 || selectedDate.getDay() === 6) {
      setPopupMessage("Callback requests are not available on weekends.");
      setShowPopup(true);
      return;
    }

    const selectedTime = selectedDate.getHours();
    if (selectedTime < 9 || selectedTime >= 17) {
      setPopupMessage(
        "Callback requests are only available between 9 am and 5 pm."
      );
      setShowPopup(true);
      return;
    }

    if (phoneNumber && isPossiblePhoneNumber(phoneNumber)) {
      const phoneNumberObject = parsePhoneNumberWithError(phoneNumber);
      const skillId =
        reasonToSkillId[reasonForCallback as keyof ReasonToSkillIdMapping];

      if (!skillId) {
        console.error("Invalid reason for callback:", reasonForCallback);
        setPopupMessage(
          "Invalid reason for callback. Please select a valid reason."
        );
        setShowPopup(true);
        return;
      }
      try {
        await axios.post("https://www.sgw-gen.com:8443/api/callback", {
          callbackUserName: name,
          callbackNumbers: phoneNumber,
          callbackScheduledTime: new Date(dateAndTime).toISOString(),
          countryCode: phoneNumberObject.country,
          skillIds: skillId,
        });
        const formattedDate = `${selectedDate
          .getDate()
          .toString()
          .padStart(2, "0")}/${(selectedDate.getMonth() + 1)
          .toString()
          .padStart(2, "0")}/${selectedDate.getFullYear()} at ${selectedDate
          .getHours()
          .toString()
          .padStart(2, "0")}:${selectedDate
          .getMinutes()
          .toString()
          .padStart(2, "0")}:${selectedDate
          .getSeconds()
          .toString()
          .padStart(2, "0")}`;
        setPopupMessage(
          `Callback request was successful and is scheduled for ${formattedDate}.`
        );
        setShowForm(false);
      } catch (error) {
        console.error("Error submitting callback:", error);
        setPopupMessage(
          "There was an error creating the callback. Please try again later."
        );
      }
      setShowPopup(true);
    } else {
      setPopupMessage("Please enter a valid phone number.");
      setShowPopup(true);
    }
  };

  const handleSuccessClosePopup = () => {
    setShowPopup(false);
    onSuccessClose();
  };

  const handleErrorClosePopup = () => {
    setShowPopup(false);
    setShowForm(true);
  };

  return (
    <>
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center">
        <div className="relative bg-white p-6 rounded-lg shadow-xl">
          <button
            onClick={onClose}
            className="absolute top-0 right-0 mt-2 mr-2 bg-transparent hover:bg-gray-200 text-gray-800 font-semibold py-1 px-2 rounded"
          >
            X
          </button>
          <div className="flex flex-col items-start space-y-4">
            <h2 className="text-lg font-bold">Request A Callback</h2>
            <form onSubmit={handleSubmit} className="w-full">
              <label htmlFor="name" className="block">
                Name:
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 p-2 border rounded w-full"
                />
              </label>
              <label htmlFor="phone" className="block">
                Phone Number:
                <PhoneInput
                  international
                  defaultCountry="IE"
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  className="mt-1 p-2 border rounded w-full"
                />
              </label>
              <label htmlFor="datetime" className="block">
                Date & Time:
                <input
                  type="datetime-local"
                  id="datetime"
                  value={dateAndTime}
                  onChange={(e) => setDateAndTime(e.target.value + ":00")}
                  className="mt-1 p-2 border rounded w-full"
                />
              </label>
              <label htmlFor="reasonForCallback" className="block">
                Reason for Callback:
                <select
                  id="reasonForCallback"
                  value={reasonForCallback}
                  onChange={(e) => setReasonForCallback(e.target.value)}
                  className="mt-1 p-2 border rounded w-full"
                >
                  <option value="">Select a reason</option>
                  {Object.keys(reasonToSkillId).map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex justify-end space-x-4 mt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="absolute bottom-6 left-6 bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div>
        {showPopup && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center">
            <div className="bg-white p-6 rounded-lg shadow-xl ">
              <p>{popupMessage}</p>
              <div className="flex justify-center mt-4">
                <button
                  onClick={
                    popupMessage.includes("successful")
                      ? handleSuccessClosePopup
                      : handleErrorClosePopup
                  }
                  className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded flex justify-center items-center"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CallbackForm;
export {};
